import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconDots, IconEye, IconPencil, IconPlus, IconSearch, IconTrash, IconX } from "@tabler/icons-react"
import { useState } from "react"

interface User {
  id: number
  name: string
  avatar_url: string | null
  active: boolean
}

interface Project {
  id: number
  name: string
  key: string | null
  description: string | null
  project_type: string
  status: string
  start_date: string | null
  end_date: string | null
  customer_name: string
  customer_id: number
  assigned_users: User[]
  hour_cap: number | null
  monthly_revenue: number
  total_tracked_hours: number
}

interface CustomerOption {
  id: number
  name: string
}

interface Props {
  [key: string]: unknown
  projects: Project[]
  pagy: PagyMeta
  filters: {
    search?: string
    project_type?: string
    status?: string
    customer_id?: string
  }
  customers: CustomerOption[]
  auth: { is_admin: boolean }
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  in_progress: "default",
  planning: "secondary",
  completed: "outline",
  on_hold: "destructive",
  cancelled: "destructive",
}

const PROJECT_TYPES = [
  { value: "fixed_price", label: "Fixed Price" },
  { value: "time_and_materials", label: "Time & Materials" },
]

const PROJECT_STATUSES = [
  { value: "planning", label: "Planning" },
  { value: "active", label: "Active" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
]

function humanize(str: string) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function ProjectsIndex() {
  const { projects, pagy, filters, customers, auth } = usePage<Props>().props
  const [search, setSearch] = useState(filters.search || "")

  function applyFilters(overrides: Record<string, string | undefined>) {
    const params: Record<string, string | undefined> = {
      search: filters.search,
      project_type: filters.project_type,
      status: filters.status,
      customer_id: filters.customer_id,
      ...overrides,
    }
    // Remove undefined/empty values
    const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""))
    router.get("/projects", cleaned, { preserveState: true })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({ search: search || undefined })
  }

  function clearFilters() {
    setSearch("")
    router.get("/projects", {}, { preserveState: true })
  }

  function handleDelete(id: number) {
    if (confirm("Delete this project? Invoices and time entries will block deletion.")) {
      router.delete(`/projects/${id}`)
    }
  }

  const hasFilters = filters.search || filters.project_type || filters.status || filters.customer_id

  return (
    <>
      <Head title="Projects - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Projects</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Projects</h1>
          {auth.is_admin && (
            <Button size="sm" asChild>
              <a href="/projects/new">
                <IconPlus className="mr-1 h-4 w-4" />
                New Project
              </a>
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <form onSubmit={handleSearch} className="flex gap-2 lg:max-w-sm">
            <div className="relative flex-1">
              <IconSearch className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search projects..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={filters.project_type || ""}
              onValueChange={(v) => applyFilters({ project_type: v || undefined })}
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder="Project Type" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.status || ""} onValueChange={(v) => applyFilters({ status: v || undefined })}>
              <SelectTrigger size="sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.customer_id || ""}
              onValueChange={(v) => applyFilters({ customer_id: v || undefined })}
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder="Customer" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <IconX className="mr-1 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Employees</TableHead>
              {auth.is_admin && <TableHead className="text-right">Monthly Revenue</TableHead>}
              <TableHead className="text-right">Hours Tracked</TableHead>
              <TableHead>Dates</TableHead>
              {auth.is_admin && (
                <TableHead className="w-10">
                  <span className="sr-only">Actions</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>
                  {auth.is_admin ? (
                    <a href={`/projects/${project.id}`} className="font-medium hover:underline">
                      {project.name}
                    </a>
                  ) : (
                    <span className="font-medium">{project.name}</span>
                  )}
                  {project.key && <span className="ml-2 font-mono text-xs text-muted-foreground">{project.key}</span>}
                </TableCell>
                <TableCell>
                  <a href={`/customers/${project.customer_id}`} className="hover:underline">
                    {project.customer_name}
                  </a>
                </TableCell>
                <TableCell>{humanize(project.project_type)}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[project.status] || "outline"}>{humanize(project.status)}</Badge>
                </TableCell>
                <TableCell>
                  {project.assigned_users.length > 0 ? (
                    <AvatarGroup>
                      {[...project.assigned_users]
                        .sort((a, b) => Number(b.active) - Number(a.active))
                        .slice(0, 4)
                        .map((user) => (
                          <Avatar
                            key={user.id}
                            size="sm"
                            title={user.active ? user.name : `${user.name} (former)`}
                            className={user.active ? "" : "opacity-40 grayscale"}
                          >
                            {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={user.name} /> : null}
                            <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                          </Avatar>
                        ))}
                      {project.assigned_users.length > 4 && (
                        <AvatarGroupCount>+{project.assigned_users.length - 4}</AvatarGroupCount>
                      )}
                    </AvatarGroup>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                {auth.is_admin && (
                  <TableCell className="text-right font-mono text-sm">
                    {project.monthly_revenue > 0 ? (
                      `$${project.monthly_revenue.toFixed(2)}`
                    ) : (
                      <span className="text-muted-foreground">$0.00</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-right font-mono text-sm">
                  {project.total_tracked_hours > 0 ? (
                    `${project.total_tracked_hours.toFixed(1)}h`
                  ) : (
                    <span className="text-muted-foreground">0h</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {formatDate(project.start_date)}
                  {project.end_date && (
                    <>
                      <br />
                      <span className="text-muted-foreground text-xs">to {formatDate(project.end_date)}</span>
                    </>
                  )}
                </TableCell>
                {auth.is_admin && (
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <IconDots className="h-4 w-4" />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <a href={`/projects/${project.id}`}>
                            <IconEye className="mr-2 h-4 w-4" />
                            View
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={`/projects/${project.id}/edit`}>
                            <IconPencil className="mr-2 h-4 w-4" />
                            Edit
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={() => handleDelete(project.id)}>
                          <IconTrash className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {projects.length === 0 && (
              <TableRow>
                <TableCell colSpan={auth.is_admin ? 9 : 8} className="text-muted-foreground text-center">
                  {hasFilters ? "No projects match your filters." : "No projects found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

ProjectsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
